import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { studentPromotionAPI } from '../../services/api';
import Swal from 'sweetalert2';
import './BulkPromotion.css';

const BulkPromotion = () => {
  const { t } = useTranslation(['bulkPromotion', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [sourceYear, setSourceYear] = useState(new Date().getFullYear() - 1);
  const [targetYear, setTargetYear] = useState(new Date().getFullYear());
  const [error, setError] = useState(null);

  // Auto-update target year when source year changes
  useEffect(() => {
    setTargetYear(sourceYear + 1);
  }, [sourceYear]);

  useEffect(() => {
    if (user?.schoolCode) {
      loadOverview();
    }
  }, [user?.schoolCode, sourceYear, targetYear]);

  const loadOverview = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading promotion overview:', {
        schoolCode: user.schoolCode,
        sourceYear,
        targetYear
      });

      const response = await studentPromotionAPI.getOverview(
        user.schoolCode,
        sourceYear,
        targetYear
      );

      console.log('Overview response:', response);

      if (response.success) {
        setOverview(response.data);
      } else {
        setError(response.message || t('bulkPromotion:errors.loadOverview'));
      }
    } catch (err) {
      console.error('Error loading overview:', err);
      setError(err.response?.data?.message || t('bulkPromotion:errors.loadOverview'));
    } finally {
      setLoading(false);
    }
  };

  const handleProcessTahun = async (tahun) => {
    const tahunData = overview?.tahunSummary.find(ts => ts.tahun === tahun);
    const tahunName = tahunData?.tahunName || `Tahun ${tahun}`;

    const result = await Swal.fire({
      title: t('bulkPromotion:confirmProcess.title'),
      html: t('bulkPromotion:confirmProcess.message', {
        tahunName,
        sourceYear,
        targetYear
      }),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: t('bulkPromotion:process'),
      cancelButtonText: t('bulkPromotion:buttons.cancel')
    });

    if (result.isConfirmed) {
      navigate(`/bulk-promotion/process/${tahun}`, {
        state: {
          schoolCode: user.schoolCode,
          schoolName: overview?.schoolName,
          sourceYear,
          targetYear,
          tahunName
        }
      });
    }
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      years.push(i);
    }
    return years;
  };

  if (loading) {
    return (
      <div className="bulk-promotion-container">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bulk-promotion-container">
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
          <button onClick={loadOverview} className="btn btn-primary">
            {t('common:retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-promotion-container">
      {/* Page Header */}
      <div className="page-header">
        <h1>
          <i className="fas fa-level-up-alt"></i> {t('bulkPromotion:title')}
        </h1>
      </div>

      {/* School Info */}
      {overview && (
        <div className="school-info-box">
          <p>
            <strong>{t('common:school')}:</strong> {overview.schoolCode} - {overview.schoolName}
          </p>
        </div>
      )}

      {/* Year Selection */}
      <div className="year-selection-box">
        <h3>
          <i className="fas fa-calendar-alt"></i> {t('bulkPromotion:selectYears')}
        </h3>
        <div className="year-selectors">
          <div className="year-selector">
            <label>{t('bulkPromotion:sourceYear')}</label>
            <select
              className="form-control"
              value={sourceYear}
              onChange={(e) => setSourceYear(parseInt(e.target.value))}
            >
              {generateYearOptions().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="year-arrow">
            <i className="fas fa-arrow-right"></i>
          </div>

          <div className="year-selector">
            <label>{t('bulkPromotion:targetYear')}</label>
            <input
              type="text"
              className="form-control"
              value={targetYear}
              readOnly
              disabled
              style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
            />
          </div>
        </div>

        <div className="year-warning">
          <i className="fas fa-info-circle"></i>
          {t('bulkPromotion:warnings.noYear')}
        </div>
      </div>

      {/* Tahun Grid */}
      <div className="tahun-selection-box">
        <h3>
          <i className="fas fa-layer-group"></i> {t('bulkPromotion:selectGrade')}
        </h3>
        <div className="tahun-grid">
          {overview?.tahunSummary && overview.tahunSummary.length > 0 ? (
            overview.tahunSummary.map((tahunData) => (
              <div key={tahunData.tahun} className="tahun-card">
                <div className="tahun-header">
                  <h4>{tahunData.tahunName}</h4>
                </div>

                <div className="tahun-stats">
                  <div className="stat-row total">
                    <span className="stat-label">
                      {tahunData.totalStudents} {t('bulkPromotion:students')}
                    </span>
                  </div>

                  <div className="stat-row progress">
                    <span className="stat-label">
                      <i className="fas fa-check-circle"></i>{' '}
                      {t('bulkPromotion:progress', {
                        processed: tahunData.processedCount,
                        total: tahunData.totalStudents
                      })}
                    </span>
                  </div>

                  {tahunData.processedCount > 0 && (
                    <div className="breakdown">
                      {tahunData.promotedCount > 0 && (
                        <div className="breakdown-item promoted">
                          <i className="fas fa-arrow-up"></i> {tahunData.promotedCount} {t('bulkPromotion:status.promoted')}
                        </div>
                      )}
                      {tahunData.repeatedCount > 0 && (
                        <div className="breakdown-item repeated">
                          <i className="fas fa-redo"></i> {tahunData.repeatedCount} {t('bulkPromotion:status.repeated')}
                        </div>
                      )}
                      {tahunData.graduatedCount > 0 && (
                        <div className="breakdown-item graduated">
                          <i className="fas fa-graduation-cap"></i> {tahunData.graduatedCount} {t('bulkPromotion:status.graduated')}
                        </div>
                      )}
                      {tahunData.transferredOutCount > 0 && (
                        <div className="breakdown-item transferred">
                          <i className="fas fa-exchange-alt"></i> {tahunData.transferredOutCount} {t('bulkPromotion:status.transferredOut')}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="tahun-footer">
                  <button
                    className={`btn btn-process ${tahunData.processedCount === tahunData.totalStudents ? 'completed' : ''}`}
                    onClick={() => handleProcessTahun(tahunData.tahun)}
                    disabled={tahunData.totalStudents === 0}
                  >
                    {tahunData.processedCount === tahunData.totalStudents ? (
                      <>
                        <i className="fas fa-check"></i> {t('bulkPromotion:done')}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-play"></i> {t('bulkPromotion:process')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data">
              <i className="fas fa-inbox"></i>
              <p>{t('bulkPromotion:warnings.noStudents')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkPromotion;
